import './SliderA.scss';

import { b2x } from '@b2x/react/src';
import classnames from 'classnames';

import { Button } from '../Button';
import { Container } from '../Container';
import { SliderASlideContentSection, SliderASlideContentSectionByShippingCountry } from '../contentTypes';
import { Icon, isIconName } from '../Icon';

export interface SliderAContentType extends b2x.contentTypes.SwiperContentSection<SliderASlideContentSection> {}
interface SliderAProps extends SliderAContentType {
  directoryCode?: string;
}

const SliderAFromDirectory = ({
  directoryCode,
  options,
}: {
  directoryCode: string;
  options: SliderAProps['options'];
}) => {
  const { shippingCountry } = b2x.useAppContext();

  const directory = b2x.useDirectory<unknown, SliderASlideContentSectionByShippingCountry>(directoryCode, {
    populate: { additionalContents: true, content: true },
  });

  const slides = directory?.additionalContents
    ?.map((additionalContent, index) => {
      const contentSection = b2x.getContentSectionByShippingCountry([additionalContent.body], shippingCountry);
      if (contentSection) {
        return {
          ...contentSection,
          contentSectionId: index,
        };
      }
      return undefined;
    })
    .filter((slide): slide is NonNullable<typeof slide> => slide !== undefined);

  return <SliderAContent options={options} slides={slides} />;
};

export const SliderAContent = ({ options, slides }: SliderAProps) => {
  const { getPagePath } = b2x.useAppStaticContext();

  const currentBreakpoint = b2x.useBreakpoint();
  const isMobile = b2x.untilBreakpoint('md', currentBreakpoint);

  return (
    <div className="slider-a position-relative" style={{ display: 'block' }}>
      <b2x.EqualHeight>
        <b2x.SwiperContext>
          {({ navigationNextElRef, navigationPrevElRef, swiper }) => (
            <b2x.SwiperFromContent
              className="slider-a"
              navigation={{ custom: true }}
              options={options}
              pagination={{ clickable: true, custom: false }}
              parallax
              slides={slides?.map(({ asset, contentSectionId, copy }, index) => {
                const alignment =
                  copy?.options?.alignment === 'end'
                    ? 'end'
                    : copy?.options?.alignment === 'center'
                    ? 'center'
                    : 'start';

                const size: b2x.ColSize =
                  copy?.options?.size === 'small'
                    ? 4
                    : copy?.options?.size === 'large'
                    ? 8
                    : copy?.options?.size === 'extra-large'
                    ? 12
                    : 6;
                return (
                  <b2x.EqualHeightElement key={contentSectionId} name="slide">
                    <b2x.Div display="grid" style={{ position: 'relative' }}>
                      {asset?.type && (
                        <b2x.Div className="asset-container">
                          <b2x.ConditionalWrapper
                            condition={copy?.cta !== undefined}
                            wrapper={
                              <b2x.router.Link
                                to={
                                  copy?.cta?.to?.href
                                    ? copy.cta.to.href
                                    : copy?.cta?.to?.code && getPagePath(copy.cta.to.code)
                                }
                              />
                            }
                          >
                            <b2x.AssetV2 {...asset} fluid />
                          </b2x.ConditionalWrapper>
                        </b2x.Div>
                      )}
                      <b2x.Div
                        alignItems={'center'}
                        className={classnames(
                          'copy-container',
                          copy?.options?.color ? `text-${copy.options.color}` : 'text-white'
                        )}
                        style={{
                          background: isMobile ? copy?.options?.background?.mobile : undefined,
                          gridRow: isMobile ? 2 : undefined,
                        }}
                      >
                        <Container className="h-100">
                          <b2x.Div
                            className="h-100 main-text-container"
                            display="grid"
                            paddingBottom={{ lg: 0, xs: 5 }}
                            paddingTop={{ lg: 0, xs: 3 }}
                            textAlign={{ lg: alignment, xs: 'center' }}
                          >
                            <b2x.Row className="main-text-row" justifyContent={{ lg: alignment, xs: 'center' }}>
                              <b2x.Col size={{ lg: size, xs: 12 }}>
                                <b2x.Div
                                  background={!isMobile ? copy?.options?.background?.desktop : undefined}
                                  padding={{ lg: copy?.options?.background?.desktop ? 5 : 0 }}
                                >
                                  <b2x.P
                                    className="title"
                                    data-swiper-parallax="-400"
                                    data-swiper-parallax-duration="500"
                                    marginBottom={{ xs: 3 }}
                                    marginTop={{ lg: 0, xs: 3 }}
                                  >
                                    {b2x.formatHtml(copy?.title)}
                                  </b2x.P>
                                  {copy?.content && (
                                    <b2x.P
                                      className="subtitle"
                                      data-swiper-parallax="-400"
                                      data-swiper-parallax-duration="550"
                                      marginBottom={{ xs: 4 }}
                                    >
                                      {b2x.formatHtml(copy.content)}
                                    </b2x.P>
                                  )}
                                  {copy?.cta && (
                                    <b2x.CtaFromContent
                                      {...copy.cta}
                                      data-swiper-parallax="-400"
                                      data-swiper-parallax-duration="600"
                                    />
                                  )}
                                </b2x.Div>
                              </b2x.Col>
                            </b2x.Row>
                            {copy?.disclaimer && (
                              <b2x.Div
                                alignItems="end"
                                className="disclaimer-container"
                                display="flex"
                                justifyContent={{ lg: alignment, xs: 'center' }}
                                marginBottom={{ lg: 4, xs: 0 }}
                                marginTop={{ lg: 0, xs: 5 }}
                              >
                                <b2x.P className="disclaimer fw-light small" margin={0}>
                                  {b2x.formatHtml(copy.disclaimer)}
                                </b2x.P>
                              </b2x.Div>
                            )}
                          </b2x.Div>
                        </Container>
                      </b2x.Div>
                    </b2x.Div>
                  </b2x.EqualHeightElement>
                );
              })}
            >
              <b2x.Div
                className="slider-arrows"
                display={{ lg: 'flex', xs: 'none' }}
                style={{ pointerEvents: 'none', zIndex: 2 }}
              >
                <Container>
                  {b2x.appConfig.icons?.slider?.arrow?.left && isIconName(b2x.appConfig.icons.slider.arrow.left) && (
                    <b2x.Div
                      className="slider-arrow-container slider-arrow-left-container"
                      display={swiper?.isBeginning ? 'none' : 'block'}
                      style={{ pointerEvents: 'all', transform: 'translateY(-50%) translateX(0)' }}
                    >
                      <Button innerRef={navigationPrevElRef} variant="blank">
                        <Icon
                          className="slider-arrow-left text-dark"
                          name={b2x.appConfig.icons.slider.arrow.left}
                          size={50}
                        />
                      </Button>
                    </b2x.Div>
                  )}
                  {b2x.appConfig.icons?.slider?.arrow?.right && isIconName(b2x.appConfig.icons.slider.arrow.right) && (
                    <b2x.Div
                      className="slider-arrow-container slider-arrow-right-container"
                      display={swiper?.isEnd ? 'none' : 'block'}
                      style={{ pointerEvents: 'all', transform: 'translateY(-50%) translateX(0)' }}
                    >
                      <Button innerRef={navigationNextElRef} variant="blank">
                        <Icon
                          className="slider-arrow-right text-dark"
                          name={b2x.appConfig.icons.slider.arrow.right}
                          size={50}
                        />
                      </Button>
                    </b2x.Div>
                  )}
                </Container>
              </b2x.Div>
            </b2x.SwiperFromContent>
          )}
        </b2x.SwiperContext>
      </b2x.EqualHeight>
    </div>
  );
};

export const SliderA = ({ directoryCode, options, slides }: SliderAProps) => {
  if (directoryCode) {
    return <SliderAFromDirectory directoryCode={directoryCode} options={options} />;
  }

  return <SliderAContent options={options} slides={slides} />;
};
